import React from 'react';

import { TargetingValues } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/apis';
import {
  AccountPermissions,
  ProviderPermissions,
} from '@provider-portal/common/services/types/account';
import { cloneDeep } from 'lodash';

import { Facade } from '@bolteu/lattice';

import navConfig from './navBarStructure.json';
import {
  ConfigMenu,
  ConfigMenuCategory,
  ConfigMenuItem,
  ConfigTabs,
  isMenuItem,
  isMenuLink,
  MenuEntryPermissions,
} from './navTypes';
import tabsConfig from './tabsStructure.json';
import { ModuleId } from '@provider-portal/common/Module';
import { TabHeaderProps } from 'components/merchant-portal-2/TabHeader/TabHeader';

export const mergeConfigAndManifest = (
  moduleManifests: Facade.ModuleManifest[],
): ConfigMenuCategory[] => {
  // Clone object
  const mergedConfig: ConfigMenuCategory[] = cloneDeep(navConfig.menu) as ConfigMenuCategory[];
  for (const manifest of moduleManifests) {
    for (const item of manifest.header.menu) {
      const navConfigEntry = mergedConfig.find((entry) => entry.id === item.title);

      if (navConfigEntry && isMenuItem(item)) {
        for (const child of item.children) {
          if (isMenuLink(child)) {
            navConfigEntry.items.push({
              // extract the second entry of the URL link
              id: child.link?.split('/', 3)?.[2] ?? '',
              titleIntlId: child.title,
              href: child.link,
              permission: child.permission as MenuEntryPermissions,
              order: child.extras?.order,
              experiment: child.extras?.experiment,
            });
          }
        }
      }
    }
  }

  return mergedConfig;
};

const validatePermissions = (
  activePermissions: ProviderPermissions,
  requiredPermissions: MenuEntryPermissions,
) => {
  const activePermissionObj: Partial<ProviderPermissions> = activePermissions ?? {};

  if (!requiredPermissions?.type || !requiredPermissions.values?.length) {
    return true;
  }

  if (requiredPermissions.type === 'all') {
    return requiredPermissions.values.every((permKey) => activePermissionObj?.[permKey]);
  }
  if (requiredPermissions.type === 'oneOf') {
    return requiredPermissions.values.some((permKey) => activePermissionObj?.[permKey]);
  }
  if (requiredPermissions.type === 'noneOf') {
    return requiredPermissions.values.every((permKey) => !activePermissionObj?.[permKey]);
  }

  return true;
};

const filterMenuCategory = (
  menuCategory: ConfigMenuCategory,
  activePermissions: ProviderPermissions,
) => {
  if (menuCategory.hidden) {
    return false;
  }
  if (!validatePermissions(activePermissions, menuCategory.permission)) {
    return false;
  }

  return true;
};

const filterMenuItem = (menuItem: ConfigMenuItem, activePermissions: ProviderPermissions) => {
  if (menuItem.hidden) {
    return false;
  }
  if (!validatePermissions(activePermissions, menuItem.permission)) {
    return false;
  }

  return true;
};

const extendProviderPermissions = (
  providerPermissions: ProviderPermissions,
  targetingParameters: TargetingValues,
  isAccountOnboarding: boolean,
): AccountPermissions => {
  const extendedProviderPermissions = { ...providerPermissions };

  // Enable Menu Editor in sidebar
  if (targetingParameters?.provider_onboarding?.withMenuEditor && isAccountOnboarding) {
    extendedProviderPermissions.menuEditorAccess = true;
  }

  return extendedProviderPermissions;
};

const filterMenuByExperiment = (
  menu: ConfigMenuCategory[],
  experiment?: { name: string; variant: string },
) => {
  if (!experiment) {
    return menu;
  }
  let experimentVariant = experiment.variant;
  const availableVariants = [];
  //check if variant exists in options, otherwise use control.
  for (const category of menu) {
    for (const item of category.items) {
      if (item.experiment && item.experiment[experiment.name]) {
        availableVariants.push(item.experiment[experiment.name]);
      }
    }
  }
  if (!availableVariants.includes(experiment.variant)) {
    experimentVariant = 'control';
  }

  const filteredMenu = [];
  for (const category of menu) {
    category.items = category.items.filter((menuItem) => {
      if (!menuItem.experiment || !menuItem.experiment[experiment.name]) {
        return true;
      }
      return menuItem.experiment?.[experiment.name] === experimentVariant;
    });
    filteredMenu.push(category);
  }
  return filteredMenu;
};

// DPX-2882: temporary solution before proper permissions system to only show specific page(s) for specific role
const filterMenuForExclusivePermissions = (
  menu: ConfigMenuCategory[],
  exclusivePermissions: Partial<ProviderPermissions>,
) => {
  if (!Object.values(exclusivePermissions).filter((value) => value).length) {
    return menu;
  }

  const filteredMenu = [];
  for (const category of menu) {
    const isExclusiveCategory =
      category.permission?.values.some((permission) => exclusivePermissions[permission]) ||
      // Settings module should be always visible
      category.id === ModuleId.Settings ||
      false;

    if (!isExclusiveCategory) {
      category.items = category.items.filter((menuItem) =>
        menuItem.permission?.values.some((permission) => exclusivePermissions[permission]),
      );
    }

    if (isExclusiveCategory || category.items.length) {
      filteredMenu.push(category);
    }
  }

  return filteredMenu;
};

export const buildMenuFromConfigAndModules = (
  moduleManifests: Facade.ModuleManifest[],
  providerPermissions: ProviderPermissions,
  exclusivePermissions: Partial<ProviderPermissions>,
  targetingParameters: TargetingValues,
  isAccountOnboarding: boolean,
  experiment?: { name: string; variant: string },
): ConfigMenuCategory[] => {
  const extendedProviderPermissions = extendProviderPermissions(
    providerPermissions,
    targetingParameters,
    isAccountOnboarding,
  );

  const fullMenu = mergeConfigAndManifest(moduleManifests);

  const menuFilteredByExperiment = filterMenuByExperiment(fullMenu, experiment);

  const menuAfterExclusivityFiltering = filterMenuForExclusivePermissions(
    menuFilteredByExperiment,
    exclusivePermissions,
  );

  // Filter menu categories (sections)
  const menuFilteredByPermissions = menuAfterExclusivityFiltering.filter((menuCat) =>
    filterMenuCategory(menuCat, extendedProviderPermissions),
  );

  // Filter menu items (links)
  for (const menuCat of menuFilteredByPermissions) {
    menuCat.items = menuCat.items.filter((menuItem) =>
      filterMenuItem(menuItem, extendedProviderPermissions),
    );
  }
  // Remove empty categories
  return menuFilteredByPermissions.filter((cat) => cat.items.length);
};

export const MenuContext = React.createContext<ConfigMenu>(null);

export interface ActiveTabHeader extends TabHeaderProps {
  associatedMenuEntryId: string;
}

export const getFilteredTabHeaderForRoute = (
  pathname: string,
  permissions: ProviderPermissions,
): ActiveTabHeader | null => {
  const config = tabsConfig as ConfigTabs;
  // Find all tab headers whose tabs match the current route
  const matchingTabHeaders = config.tabHeaders.filter((tabHeader) => {
    return tabHeader.tabs.some(
      (tab) => pathname === tab.route || pathname.startsWith(`${tab.route}/`),
    );
  });

  // Try each matching tab header until one passes the permission check
  for (const matchingTabHeader of matchingTabHeaders) {
    if (!validatePermissions(permissions, matchingTabHeader.permission)) {
      continue;
    }

    const filteredTabs = matchingTabHeader.tabs.filter((tab) =>
      validatePermissions(permissions, tab.permission),
    );

    if (!filteredTabs.length) {
      continue;
    }

    return {
      id: matchingTabHeader.id,
      titleIntlId: matchingTabHeader.titleIntlId,
      tabs: filteredTabs,
      associatedMenuEntryId: matchingTabHeader.associatedMenuEntryId,
    };
  }

  return null;
};
